package cz.lhoracek.mapl.ui.screens.session

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.background
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.IconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.foundation.Image
import cz.lhoracek.mapl.data.model.FLEK_NAMES
import cz.lhoracek.mapl.data.model.Player
import cz.lhoracek.mapl.data.model.Round
import cz.lhoracek.mapl.data.model.SessionStatus
import cz.lhoracek.mapl.data.model.Suit
import androidx.compose.ui.tooling.preview.Preview
import cz.lhoracek.mapl.data.model.GameType
import cz.lhoracek.mapl.data.model.Session
import cz.lhoracek.mapl.data.model.SessionWithPlayers
import cz.lhoracek.mapl.domain.nextBetPowerOfTwo
import cz.lhoracek.mapl.domain.previousBetPowerOfTwo
import cz.lhoracek.mapl.domain.normalizeBetToPowerOfTwo
import cz.lhoracek.mapl.domain.SessionScoreCalculator
import cz.lhoracek.mapl.ui.components.CommitPendingDeleteOnDispose
import cz.lhoracek.mapl.ui.components.DismissibleEndToStartItem
import cz.lhoracek.mapl.ui.components.DeleteDismissBackground
import cz.lhoracek.mapl.ui.components.MaplTopAppBar
import cz.lhoracek.mapl.ui.components.NumberStepper
import cz.lhoracek.mapl.ui.components.PlayerAvatar
import cz.lhoracek.mapl.ui.components.QrCodeImage
import cz.lhoracek.mapl.ui.components.UndoDeleteSnackbarEffect
import cz.lhoracek.mapl.ui.theme.MaplTheme
import mapl.composeapp.generated.resources.*
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SessionScreen(
    sessionId: String,
    onBack: () -> Unit,
    onAddRound: () -> Unit,
) {
    val viewModel: SessionViewModel = koinViewModel(
        key = "session_$sessionId",
        parameters = { parametersOf(sessionId) },
    )
    val sessionData by viewModel.session.collectAsState()
    val pendingDeleteRound by viewModel.pendingDeleteRound.collectAsState()
    val lastUndoneRoundId by viewModel.lastUndoneRoundId.collectAsState()

    SessionScreenContent(
        sessionId = sessionId,
        sessionData = sessionData,
        pendingDeleteRound = pendingDeleteRound,
        lastUndoneRoundId = lastUndoneRoundId,
        onBack = onBack,
        onAddRound = onAddRound,
        onUpdateBet = viewModel::updateBet,
        onEndSession = viewModel::endSession,
        onReactivateSession = viewModel::reactivateSession,
        onMarkForDelete = viewModel::markForDelete,
        onCancelDelete = viewModel::cancelDelete,
        onCommitDelete = viewModel::commitDelete,
        onClearUndoSignal = viewModel::clearUndoSignal,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun SessionScreenContent(
    sessionId: String,
    sessionData: SessionWithPlayers?,
    pendingDeleteRound: Round?,
    lastUndoneRoundId: String?,
    onBack: () -> Unit,
    onAddRound: () -> Unit,
    onUpdateBet: (Int) -> Unit,
    onEndSession: () -> Unit,
    onReactivateSession: () -> Unit,
    onMarkForDelete: (Round) -> Unit,
    onCancelDelete: () -> Unit,
    onCommitDelete: () -> Unit,
    onClearUndoSignal: () -> Unit,
) {
    var showBetSheet by remember { mutableStateOf(false) }
    var showEndConfirm by remember { mutableStateOf(false) }
    var showQr by remember { mutableStateOf(false) }

    val snackbarHostState = remember { SnackbarHostState() }
    val deletedMsg = stringResource(Res.string.session_round_deleted)
    val backMsg = stringResource(Res.string.back)

    UndoDeleteSnackbarEffect(
        trigger = pendingDeleteRound?.id,
        enabled = pendingDeleteRound != null,
        snackbarHostState = snackbarHostState,
        message = deletedMsg,
        actionLabel = backMsg,
        onUndo = onCancelDelete,
        onDismissed = onCommitDelete,
    )
    CommitPendingDeleteOnDispose(onCommitDelete)

    if (showBetSheet && sessionData != null) {
        BetBottomSheet(
            currentBet = sessionData.session.currentBet,
            onDismiss = { showBetSheet = false },
            onConfirm = { newBet ->
                onUpdateBet(newBet)
                showBetSheet = false
            },
        )
    }

    if (showQr) {
        AlertDialog(
            onDismissRequest = { showQr = false },
            containerColor = MaterialTheme.colorScheme.surface,
            title = { Text(stringResource(Res.string.session_share)) },
            text = {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    QrCodeImage(
                        content = "mapl://join/$sessionId",
                        size = 240.dp,
                        darkColor = MaterialTheme.colorScheme.onSurface,
                        lightColor = MaterialTheme.colorScheme.surface,
                    )
                    Spacer(Modifier.height(12.dp))
                    Text(
                        text = sessionId,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = TextAlign.Center,
                    )
                }
            },
            confirmButton = {
                TextButton(onClick = { showQr = false }) { Text(stringResource(Res.string.close)) }
            },
        )
    }

    if (showEndConfirm) {
        AlertDialog(
            onDismissRequest = { showEndConfirm = false },
            containerColor = MaterialTheme.colorScheme.surface,
            title = { Text(stringResource(Res.string.session_end_title)) },
            text = { Text(stringResource(Res.string.session_end_confirm)) },
            confirmButton = {
                TextButton(onClick = {
                    onEndSession()
                    showEndConfirm = false
                    onBack()
                }) { Text(stringResource(Res.string.end)) }
            },
            dismissButton = {
                TextButton(onClick = { showEndConfirm = false }) { Text(stringResource(Res.string.cancel)) }
            },
        )
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            MaplTopAppBar(
                title = sessionData?.session?.name ?: stringResource(Res.string.session_title),
                onBack = onBack,
                actions = {
                    IconButton(onClick = { showQr = true }) {
                        Icon(
                            painter = painterResource(Res.drawable.ic_qr_code),
                            contentDescription = stringResource(Res.string.session_qr)
                        )
                    }
                    TextButton(onClick = { showBetSheet = true }) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                painter = painterResource(Res.drawable.ic_payments),
                                contentDescription = null,
                                modifier = Modifier.size(18.dp),
                                tint = MaterialTheme.colorScheme.onPrimary
                            )
                            Spacer(Modifier.width(4.dp))
                            Text(
                                text = "${sessionData?.session?.currentBet ?: 0}",
                                color = MaterialTheme.colorScheme.onPrimary,
                                style = MaterialTheme.typography.titleMedium
                            )
                        }
                    }
                    if (sessionData?.session?.status == SessionStatus.ACTIVE) {
                        IconButton(onClick = { showEndConfirm = true }) {
                            Icon(
                                painter = painterResource(Res.drawable.ic_stop),
                                contentDescription = stringResource(Res.string.end)
                            )
                        }
                    } else {
                        TextButton(onClick = onReactivateSession) {
                            Text(stringResource(Res.string.reactivate), color = MaterialTheme.colorScheme.onPrimary)
                        }
                    }
                },
            )
        },
        bottomBar = {
            if (sessionData?.session?.status == SessionStatus.ACTIVE) {
                Button(
                    onClick = onAddRound,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp)
                        .heightIn(min = 64.dp),
                ) {
                    Text(stringResource(Res.string.session_add_round), style = MaterialTheme.typography.titleLarge)
                }
            }
        },
    ) { innerPadding ->
        if (sessionData == null) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
            return@Scaffold
        }
        val scoreResult = remember(
            sessionData.session,
            sessionData.players,
            sessionData.rounds,
        ) {
            SessionScoreCalculator.calculate(
                session = sessionData.session,
                players = sessionData.players,
                rounds = sessionData.rounds,
            )
        }

        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(innerPadding),
        ) {
            item {
                ScoreHeader(players = sessionData.players)
                HorizontalDivider()
                if (sessionData.session.potEnabled) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 10.dp),
                    ) {
                        Text(
                            text = stringResource(Res.string.session_pot_label, scoreResult.currentPot),
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.SemiBold,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    HorizontalDivider()
                }
                if (sessionData.rounds.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().padding(32.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(stringResource(Res.string.session_no_rounds), color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                }
            }
            items(sessionData.rounds.reversed(), key = { it.id }) { round ->
                DismissibleEndToStartItem(
                    resetSignal = lastUndoneRoundId == round.id,
                    onResetHandled = onClearUndoSignal,
                    onDismissed = { onMarkForDelete(round) },
                    backgroundContent = {
                        DeleteDismissBackground()
                    },
                ) {
                    // Surface background + divider together so no errorContainer gap between items
                    Column(Modifier.background(MaterialTheme.colorScheme.surface).fillMaxWidth()) {
                        RoundRow(
                            round = round,
                            players = sessionData.players,
                            currentBet = sessionData.session.currentBet,
                            potAward = scoreResult.awardedPotByRoundId[round.id] ?: 0,
                        )
                        HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))
                    }
                }
            }
        }
    }
}

@Composable
private fun ScoreHeader(players: List<Player>) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp, vertical = 12.dp),
    ) {
        players.forEach { player ->
            PlayerScoreChip(
                player = player,
                modifier = Modifier.weight(1f),
            )
        }
    }
}

@Composable
private fun PlayerScoreChip(
    player: Player,
    modifier: Modifier = Modifier,
) {
    val isPositive = player.score >= 0
    val scoreColor = if (isPositive) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = modifier.padding(4.dp),
    ) {
        PlayerAvatar(
            name = player.name,
            colorHex = player.color,
            size = 44.dp
        )
        Spacer(Modifier.height(2.dp))
        Text(
            player.name,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = if (isPositive) "+${player.score}" else "${player.score}",
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = scoreColor,
        )
    }
}

@Composable
private fun RoundRow(
    round: Round,
    players: List<Player>,
    currentBet: Int,
    potAward: Int,
) {
    val noCallerBet = if (round.bet > 0) round.bet else currentBet
    val potAddedAmount = noCallerBet * 2 * players.size
    if (round.noCaller) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    painter = painterResource(Res.drawable.ic_payments),
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp),
                )
                Spacer(Modifier.width(8.dp))
                Column {
                    Text(
                        text = stringResource(Res.string.session_round_no_call),
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.SemiBold,
                    )
                    Text(
                        text = stringResource(Res.string.session_round_pot_added, potAddedAmount),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
        return
    }

    val actor = players.find { it.id == round.actorId }
    val flekName = FLEK_NAMES.getOrElse(round.flekLevel) { "x${round.multiplier}" }
    val betLabel = if (round.bet > 0) "(${round.bet} x ${round.multiplier})" else "(x ${round.multiplier})"
    val potAwardLabel = if (potAward > 0) " + $potAward" else ""

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            // Player avatar
            PlayerAvatar(
                name = actor?.name ?: stringResource(Res.string.unknown),
                colorHex = actor?.color ?: "#9E9E9E",
                size = 32.dp,
                textStyle = MaterialTheme.typography.labelMedium
            )
            Spacer(Modifier.width(6.dp))
            // Suit icon
            Image(
                painter = painterResource(
                    when (round.suit) {
                        Suit.ZALUDY -> Res.drawable.suit_zaludy
                        Suit.KULE -> Res.drawable.suit_kule
                        Suit.ZELENE -> Res.drawable.suit_zelene
                        Suit.LEPSI -> Res.drawable.suit_lepsi
                    }
                ),
                contentDescription = round.suit.label,
                modifier = Modifier.size(28.dp),
            )
            Spacer(Modifier.width(8.dp))
            Column {
                Text(
                    text = "${actor?.name ?: stringResource(Res.string.unknown)} - ${round.suit.label} - ${round.gameType.label}",
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    text = "$flekName $betLabel$potAwardLabel",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = if (round.won) stringResource(Res.string.session_round_won) else stringResource(Res.string.session_round_lost),
                style = MaterialTheme.typography.labelLarge,
                color = if (round.won) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error,
                fontWeight = FontWeight.Bold,
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun BetBottomSheet(
    currentBet: Int,
    onDismiss: () -> Unit,
    onConfirm: (Int) -> Unit,
) {
    var bet by remember { mutableStateOf(normalizeBetToPowerOfTwo(currentBet)) }
    val sheetState = rememberModalBottomSheetState()

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        containerColor = MaterialTheme.colorScheme.surface
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 24.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            Text(stringResource(Res.string.session_change_bet), style = MaterialTheme.typography.titleLarge)
            NumberStepper(
                valueLabel = "$bet",
                onDecrement = { bet = previousBetPowerOfTwo(bet) },
                onIncrement = { bet = nextBetPowerOfTwo(bet) },
                decrementEnabled = bet > 1,
                modifier = Modifier.fillMaxWidth(),
                valueStyle = MaterialTheme.typography.headlineMedium,
                valueContent = {
                    Text(
                        "$bet",
                        style = MaterialTheme.typography.headlineMedium,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.weight(1f),
                        textAlign = TextAlign.Center,
                    )
                },
            )
            Button(
                onClick = { onConfirm(bet) },
                modifier = Modifier.fillMaxWidth(),
                enabled = bet != currentBet,
            ) { Text(stringResource(Res.string.confirm)) }
            Spacer(Modifier.height(16.dp))
        }
    }
}

@Preview
@Composable
private fun SessionScreenPreview() {
    val players = listOf(
        Player("1", "Alice In The Black Chains", "#FF0000", 100),
        Player("2", "Bob", "#00FF00", -50),
        Player("3", "Charlie", "#0000FF", 20)
    )
    val session = Session(
        id = "s1",
        name = "Páteční mariáš",
        baseBet = 1,
        currentBet = 2,
        potEnabled = true,
        potAmount = 2,
        status = SessionStatus.ACTIVE,
        playerIds = players.map { it.id }
    )
    val rounds = listOf(
        Round("r1", "s1", "1", Suit.ZALUDY, GameType.TRI, 0, true, 1000L, bet = 1),
        Round("r2", "s1", "", Suit.ZALUDY, GameType.TRI, 0, false, 1500L, bet = 1, noCaller = true),
        Round("r3", "s1", "2", Suit.KULE, GameType.CTYRI, 1, false, 2000L, bet = 1),
    )
    val sessionData = SessionWithPlayers(session, players, rounds)

    MaplTheme {
        SessionScreenContent(
            sessionId = "s1",
            sessionData = sessionData,
            pendingDeleteRound = null,
            onBack = {},
            onAddRound = {},
            onUpdateBet = {},
            onEndSession = {},
            onReactivateSession = {},
            onMarkForDelete = {},
            onCancelDelete = {},
            onCommitDelete = {},
            onClearUndoSignal = {},
            lastUndoneRoundId = null,
        )
    }
}
